import * as React from "react"

import { announceScreenReaderMessage } from "@/components/ui/ScreenReaderLive"
import type {
  ToastActionElement,
  ToastProps,
} from "@/components/ui/toast"

const TOAST_LIMIT = 3
const TOAST_REMOVE_DELAY = 3250

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

const getScreenReaderText = (value?: React.ReactNode) => {
  if (value === null || value === undefined) {
    return ""
  }

  if (typeof value === "string") {
    return value.trim()
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value)
  }

  if (value instanceof Error) {
    return value.message?.trim() ?? ""
  }

  return ""
}

const getScreenReaderAnnouncement = (
  title?: React.ReactNode,
  description?: React.ReactNode
) => {
  const normalizedTitle = getScreenReaderText(title)
  if (normalizedTitle.length > 0) {
    return normalizedTitle
  }

  const normalizedDescription = getScreenReaderText(description)
  if (normalizedDescription.length > 0) {
    return normalizedDescription
  }

  return "Notification"
}

type Toast = Omit<ToasterToast, "id">
type ToastController = {
  id: string
  dismiss: () => void
  update: (props: ToasterToast) => void
}
type ToastFn = ((props: Toast) => ToastController) & {
  dismiss: (toastId?: string) => void
  success: (message: string, options?: Partial<Toast>) => ToastController
  error: (message: string, options?: Partial<Toast>) => ToastController
}

const toast: ToastFn = ({ ...props }: Toast) => {
  const id = genId()

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  // Announce the toast content
  const announcement = getScreenReaderAnnouncement(props.title, props.description)
  const priority = props.variant === "destructive" || props.variant === "error" ? "assertive" : "polite"
  announceScreenReaderMessage(announcement, priority)

  return {
    id: id,
    dismiss,
    update,
  }
}

toast.dismiss = (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId })

/**
 * Convenience methods for common toast types
 * These provide backward-compatible API for toast.success() and toast.error() calls
 */
toast.success = (message: string, options?: Partial<Toast>) => {
  return toast({
    title: message,
    variant: 'default',
    ...options,
  });
};

toast.error = (message: string, options?: Partial<Toast>) => {
  return toast({
    title: message,
    variant: 'destructive',
    ...options,
  });
};

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  }
}

export { useToast, toast }
